import { Insurance, PricePlan, ShadowOptin } from '@/data'
import update from 'immutability-helper'
import { StoreActionMap } from '@/store/types'
import { User } from '@/dataV3/User/User'
import { ConnectedUser } from '@/dataV3/User/ConnectedUser'
import { PendingAcquisitionUser } from '@/dataV3/User/PendingAcquisitionUser'
import { WithReactivation } from '@/modules/Auth/apiTypes/base'
import { Animal } from '@/dataV3/Animal/Animal'
import { Petsitter } from '@/dataV3/PetSitter/Petsitter'

export interface UserReducer {
  currentUser?: ConnectedUser | null
  currentPendingUser?: PendingAcquisitionUser | null
  currentPetsitter?: Petsitter | null
  reactivation: boolean
  currentShadowOptins?: ShadowOptin[]
  currentPrices?: PricePlan[]
}

export const initialUserReducerState: UserReducer = {
  currentUser: null,
  currentPendingUser: null,
  currentPetsitter: null,
  reactivation: false,
  currentShadowOptins: [],
  currentPrices: [],
}

export enum UserTypes {
  Restore = 'User/RESTORE',
  UpdateUser = 'User/UPDATE_USER',
  CreateAnimal = 'User/CREATE_ANIMAL',
  UpdateAnimal = 'User/UPDATE_ANIMAL',
  DeleteAnimal = 'User/DELETE_ANIMAL',
  UpdateShadowOptins = 'User/UPDATE_SHADOWOPTINS',
  UpdateInsurance = 'User/UPDATE_INSURANCE',
  UpdatePrices = 'User/UPDATE_PRICES',
  Logout = 'User/LOGOUT',
}

type UserPayload = {
  [UserTypes.Restore]: UserReducer
  [UserTypes.UpdateUser]: WithReactivation<User | Petsitter> | null
  [UserTypes.CreateAnimal]: Animal
  [UserTypes.UpdateAnimal]: Animal
  [UserTypes.DeleteAnimal]: string // AnimalId
  [UserTypes.UpdateShadowOptins]: ShadowOptin[]
  [UserTypes.UpdateInsurance]: Insurance
  [UserTypes.UpdatePrices]: PricePlan[]
  [UserTypes.Logout]: null
}

export type UserActions = StoreActionMap<UserPayload>[keyof StoreActionMap<UserPayload>]

const userReducer = (curState: UserReducer, action: UserActions): UserReducer => {
  switch (action.type) {
    case UserTypes.Restore:
      return {
        ...action.payload,
      }
    case UserTypes.UpdateUser:
      switch (action.payload?.['@type']) {
        case 'PendingAcquisitionUser': {
          const { reactivation = curState.reactivation, ...currentPendingUser } = action.payload
          return {
            ...curState,
            currentUser: null,
            currentPendingUser,
            currentPetsitter: null,
            reactivation,
          }
        }
        case 'ConnectedUser': {
          const { reactivation = curState.reactivation, ...currentUser } = action.payload
          return {
            ...curState,
            currentUser,
            currentPendingUser: null,
            currentPetsitter: null,
            reactivation,
          }
        }
        case 'ConnectedPetsitter':
        case 'PendingAcquisitionPetsitter': {
          const { reactivation = curState.reactivation, ...currentPetsitter } = action.payload
          return {
            ...curState,
            currentUser: null,
            currentPendingUser: null,
            currentPetsitter,
            reactivation,
          }
        }
        default: {
          return {
            ...curState,
            currentUser: null,
            currentPendingUser: null,
            currentPetsitter: null,
          }
        }
      }
    case UserTypes.CreateAnimal: {
      switch (action.payload?.['@type']) {
        case 'ConnectedUserAnimal':
          if (!curState.currentUser) {
            throw new Error('Cannot add ConnectedUserAnimal because there is no ConnectedUser')
          }

          return update(curState, {
            currentUser: {
              animals: { $push: [action.payload] },
            },
          })
        case 'PendingAcquisitionAnimal':
          if (!curState.currentPendingUser) {
            throw new Error('Cannot add PendingAcquisitionAnimal because there is no PendingAcquisitionUser')
          }
          return update(curState, {
            currentPendingUser: {
              animals: { $push: [action.payload] },
            },
          })
        default:
          return curState
      }
    }
    case UserTypes.UpdateAnimal: {
      switch (action.payload?.['@type']) {
        case 'ConnectedUserAnimal': {
          if (!curState.currentUser) {
            throw new Error('Cannot update ConnectedUserAnimal because there is no ConnectedUser')
          }

          const index = curState.currentUser.animals.findIndex((animal) => animal.id === action.payload?.id)
          if (index >= 0) {
            return update(curState, {
              currentUser: {
                animals: { $splice: [[index, 1, action.payload]] },
              },
            })
          }
          return update(curState, {
            currentUser: {
              animals: { $push: [action.payload] },
            },
          })
        }
        case 'PendingAcquisitionAnimal': {
          if (!curState.currentPendingUser) {
            throw new Error('Cannot update PendingAcquisitionAnimal because there is no PendingAcquisitionUser')
          }

          const index = curState.currentPendingUser.animals.findIndex((animal) => animal.id === action.payload?.id)
          if (index >= 0) {
            return update(curState, {
              currentPendingUser: {
                animals: { $splice: [[index, 1, action.payload]] },
              },
            })
          }
          return update(curState, {
            currentPendingUser: {
              animals: { $push: [action.payload] },
            },
          })
        }
        default:
          return curState
      }
    }
    case UserTypes.DeleteAnimal: {
      if (curState.currentUser) {
        const index = curState.currentUser.animals.findIndex((animal) => animal.id === action.payload)
        if (index >= 0) {
          return update(curState, {
            currentUser: {
              animals: { $splice: [[index, 1]] },
            },
          })
        }
      } else if (curState.currentPendingUser) {
        const index = curState.currentPendingUser.animals.findIndex((animal) => animal.id === action.payload)
        if (index >= 0) {
          return update(curState, {
            currentPendingUser: {
              animals: { $splice: [[index, 1]] },
            },
          })
        }
      }
      return curState
    }
    case UserTypes.UpdateShadowOptins:
      return {
        ...curState,
        currentShadowOptins: action.payload,
      }
    case UserTypes.UpdatePrices:
      return {
        ...curState,
        currentPrices: action.payload,
      }
    case UserTypes.Logout:
      localStorage.clear()
      return initialUserReducerState
    default:
      return curState
  }
}

export default userReducer
